<div class="finance-list-page">
    <app-page-shell>
        <p-toast position="top-center"></p-toast>
        <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

        <app-page-hero [eyebrow]="t('pages.debitNotes.hero.eyebrow')" [title]="t('pages.debitNotes.hero.title')"
            [subtitle]="t('pages.debitNotes.hero.subtitle')">
            <div actions>
                <p-button *ngIf="canAddDebitNote && !isConsolidatedReadOnly" [label]="t('pages.debitNotes.actions.addDebitNote')" icon="fa-solid fa-plus"
                    (click)="addDebitNote()"></p-button>
            </div>
        </app-page-hero>

        <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

        <app-page-search-panel [title]="t('pages.debitNotes.search.title')" [activeFilters]="activeFilterCount"
            [resultText]="t('pages.debitNotes.search.resultText')" [collapsed]="true"
            (search)="applyFilters()" (clear)="clearFilters()">
            <div class="finance-search-grid">
                <app-company-scope-selector (scopeChange)="onScopeChange($event)"></app-company-scope-selector>
            </div>
        </app-page-search-panel>

        <app-page-data-panel [title]="t('pages.debitNotes.dataPanel.title')"
            [subtitle]="t('pages.debitNotes.dataPanel.subtitle')"
            [resultText]="resultText">
            <app-accounting-directory-controls documentType="DEBIT_NOTE" [filters]="directoryFilters"
                [searchText]="searchText" [selectedStatuses]="selectedDocumentStatuses" [pendingStatuses]="pendingStatuses" (searchTextChange)="onDirectorySearch($event)"
                (modeChange)="onDirectoryModeChange($event)" (pending)="showPendingStatuses()"
                (all)="showAllStatuses()"></app-accounting-directory-controls>
            <p-table *ngIf="directoryMode === 'LIST'" class="finance-table" [value]="debitNotes" dataKey="debitNoteID" [paginator]="true"
                [rows]="rows" [first]="first" [showCurrentPageReport]="true" [totalRecords]="totalRecords"
                [lazy]="true" [currentPageReportTemplate]="currentPageReportTemplate"
                (onLazyLoad)="loadData($event)" [rowsPerPageOptions]="[20, 50, 100]"
                [tableStyle]="{ 'min-width': '0' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th>{{ t('pages.debitNotes.table.debitNote') }}</th>
                        <th>{{ t('pages.debitNotes.table.customerOriginalInvoice') }}</th>
                        <th>{{ t('pages.debitNotes.table.amountsTax') }}</th>
                        <th>{{ t('pages.debitNotes.table.workflowZatcaStatus') }}</th>
                        <th>{{ t('pages.debitNotes.table.reasonDates') }}</th>
                        <th>{{ t('pages.debitNotes.table.actions') }}</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-debitNote>
                    <tr>
                        <td [attr.data-label]="t('pages.debitNotes.table.debitNote')">
                            <div class="stack-cell">
                                <span class="cell-title">{{ debitNote.debitNoteNumber || t('pages.debitNotes.table.noDebitNoteNumber') }}</span>
                                <div class="cell-meta">
                                    <span class="code-pill">{{ t('pages.debitNotes.table.id') }} {{ debitNote.debitNoteID || '-' }}</span>
                                    <span>{{ debitNote.company?.name || '-' }}</span>
                                </div>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.debitNotes.table.customerOriginalInvoice')">
                            <div class="stack-cell">
                                <span class="cell-title">{{ debitNote.crmDisplayName || debitNote.crmClient?.companyName || debitNote.customer?.name || '-' }}</span>
                                <div class="cell-meta vertical">
                                    <span *ngIf="debitNote.crmContactDisplayName || debitNote.crmContactEmail || debitNote.crmContactPhone">
                                        {{ debitNote.crmContactDisplayName || debitNote.crmContactEmail || debitNote.crmContactPhone }}
                                    </span>
                                    <span>{{ t('pages.debitNotes.table.invoice') }} {{ debitNote.originalInvoiceNumber || debitNote.invoice?.invoiceNumber || '-' }}</span>
                                    <span>{{ debitNote.crmAddress || t('pages.debitNotes.table.addressPending') }}</span>
                                </div>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.debitNotes.table.amountsTax')">
                            <div class="amount-block">
                                <span class="amount-primary" *ngIf="debitNote.taxAmount != null">{{ formatKnownMoney(getGrossAmount(debitNote)) }}</span>
                                <span class="amount-primary" *ngIf="debitNote.taxAmount == null">{{ t('accountingAmounts.beforeTax') }} {{ formatKnownMoney(getBeforeTaxAmount(debitNote)) }}</span>
                                <span class="amount-secondary" *ngIf="debitNote.taxAmount != null">{{ t('accountingAmounts.beforeTax') }} {{ formatKnownMoney(getBeforeTaxAmount(debitNote)) }}</span>
                                <span class="amount-secondary">{{ t('pages.debitNotes.table.tax') }} {{ formatKnownMoney(debitNote.taxAmount ?? null) }}</span>
                                <span class="amount-secondary">{{ t('pages.debitNotes.table.lineCount', { count: debitNote.debitNoteLines?.length || 0 }) }}</span>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.debitNotes.table.workflowZatcaStatus')">
                            <div class="status-stack">
                                <p-tag [severity]="getStatusSeverity(getDocumentStatus(debitNote))"
                                    [value]="documentStatusLabel(getDocumentStatus(debitNote))"></p-tag>
                                <span class="muted-pill">{{ documentStatusLabel(getPostingStatus(debitNote)) }}</span>
                                <span class="muted-pill">{{ t('pages.debitNotes.table.zatca') }} {{ debitNote.zatcaStatus || t('pages.debitNotes.table.notAvailable') }}</span>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.debitNotes.table.reasonDates')">
                            <div class="stack-cell">
                                <span class="cell-title">{{ debitNote.reason || t('pages.debitNotes.table.noReason') }}</span>
                                <div class="cell-meta vertical">
                                    <span>{{ t('pages.debitNotes.table.noteDate') }} {{ debitNote.debitNoteDate | date:'yyyy-MM-dd' }}</span>
                                    <span>{{ t('pages.debitNotes.table.created') }} {{ debitNote.createdAt | date:'yyyy-MM-dd' }}</span>
                                </div>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.debitNotes.table.actions')">
                            <div class="row-actions">
                                <p-button icon="pi pi-eye" [rounded]="true" [text]="true" [pTooltip]="t('pages.debitNotes.tooltips.viewDebitNote', 'View debit note')"
                                    (click)="viewDebitNote(debitNote)"></p-button>
                                <p-button icon="pi pi-list" [rounded]="true" [text]="true" [pTooltip]="t('pages.debitNotes.tooltips.previewLines', 'Preview lines')"
                                    (click)="openPreview(debitNote)"></p-button>
                                <p-button *ngIf="canEditDebitNote && !isConsolidatedReadOnly" icon="pi pi-pencil" [rounded]="true" [text]="true"
                                    severity="info" [pTooltip]="t('pages.debitNotes.tooltips.editDebitNote', 'Edit Debit Note')" [disabled]="!isEditable(debitNote)"
                                    (click)="editDebitNote(debitNote)"></p-button>
                                <app-finance-pdf-view-button kind="debit-note" [documentId]="debitNote.debitNoteID" [documentReference]="debitNote.debitNoteNumber" [postingStatus]="debitNote.postingStatus" [iconOnly]="true" [disabled]="documentActions.isPending(debitNote.debitNoteID)"></app-finance-pdf-view-button>
                                <p-button icon="pi pi-download" [rounded]="true" [text]="true" severity="help" [loading]="documentActions.isPending(debitNote.debitNoteID)"
                                    [pTooltip]="t('pages.debitNotes.tooltips.downloadDebitNote', 'Download Debit Note')" (click)="downloadDebitNote(debitNote)"></p-button>
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="6">
                            <div class="empty-state">
                                <i class="pi pi-inbox"></i>
                                <strong>{{ t('pages.debitNotes.empty.title') }}</strong>
                                <span>{{ t('pages.debitNotes.empty.subtitle') }}</span>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </app-page-shell>

    <p-drawer [(visible)]="isPreviewVisible" position="right" styleClass="finance-preview-drawer">
        <ng-template pTemplate="header">
            <div class="stack-cell">
                <strong>{{ previewDebitNote?.debitNoteNumber || t('pages.debitNotes.preview.debitNoteLines') }}</strong>
                <span class="cell-meta">{{ t('pages.debitNotes.preview.lineCount', { count: previewDebitNote?.debitNoteLines?.length || 0 }) }}</span>
            </div>
        </ng-template>
        <div class="preview-list" *ngIf="previewDebitNote?.debitNoteLines?.length; else noDebitNoteLines">
            <div class="preview-line" *ngFor="let line of previewDebitNote?.debitNoteLines">
                <div class="preview-line-header">
                    <div>
                        <div class="preview-line-title">{{ line.description || t('pages.debitNotes.preview.lineItem') }}</div>
                    </div>
                    <div class="preview-line-amount">{{ formatMoney(line.amount, 'SAR') }}</div>
                </div>
            </div>
        </div>
        <ng-template #noDebitNoteLines>
            <div class="preview-empty">{{ t('pages.debitNotes.preview.noLines') }}</div>
        </ng-template>
    </p-drawer>
</div>
